import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useState, useMemo } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Download,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  File as FileIcon,
  Hash,
  History,
  Pencil,
  ShieldAlert,
} from "lucide-react";
import { AppShell } from "@client/components/app-shell";
import { Button } from "@client/components/ui/button";
import { Badge } from "@client/components/ui/badge";
import { Spinner } from "@client/components/ui/spinner";
import { SuperDocViewer } from "../components/superdoc-viewer";
import { ExcelViewer } from "../components/excel-viewer";
import { PdfViewer } from "../components/pdf-viewer";
import { SuperDocEditor } from "@client/components/superdoc-editor";
import { VersionHistoryPanel } from "@client/components/version-history-panel";
import { cn } from "@client/lib/cn";
import { friendlyMime } from "@client/lib/mime";

const XlsxEditor = lazy(() => import("@client/components/xlsx-editor"));

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const XLS_MIME = "application/vnd.ms-excel";

interface PresenceEditor {
  userId: string;
  name: string;
  color: string;
}

interface DocDetail {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string | null;
  status: "pending" | "processing" | "ready" | "rejected" | "quarantined";
  createdAt: string;
  downloadUrl: string | null;
  currentVersionNumber: number;
  permissions: { canEdit: boolean };
  editors: PresenceEditor[];
}

async function fetchDoc(id: string): Promise<DocDetail> {
  const res = await fetch(`/api/documents/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error("Documento não encontrado");
  return res.json();
}

export const Route = createFileRoute("/documents/$id")({
  beforeLoad: async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) throw redirect({ to: "/login" });
  },
  component: DocViewPage,
});

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function fileIconLarge(mime: string) {
  const cls = "h-6 w-6";
  if (mime.startsWith("image/"))
    return <FileImage className={cn(cls, "text-[var(--color-info)]")} />;
  if (mime === "application/pdf")
    return <FileText className={cn(cls, "text-[var(--color-danger)]")} />;
  if (mime.includes("spreadsheet"))
    return <FileSpreadsheet className={cn(cls, "text-[var(--color-success)]")} />;
  if (mime.includes("word") || mime.includes("document"))
    return <FileType className={cn(cls, "text-[var(--color-info)]")} />;
  return <FileIcon className={cn(cls, "text-[var(--color-ink-muted)]")} />;
}

function statusBadge(s: DocDetail["status"]) {
  const config = {
    pending: { variant: "outline" as const, label: "Aguardando" },
    processing: { variant: "warning" as const, label: "Verificando" },
    ready: { variant: "success" as const, label: "Pronto" },
    rejected: { variant: "danger" as const, label: "Rejeitado" },
    quarantined: { variant: "danger" as const, label: "Quarentena" },
  };
  const c = config[s];
  return (
    <Badge variant={c.variant}>
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          c.variant === "success" && "bg-[var(--color-success)]",
          c.variant === "warning" && "bg-[var(--color-warning)]",
          c.variant === "danger" && "bg-[var(--color-danger)]",
          c.variant === "outline" && "bg-[var(--color-ink-faint)]",
        )}
      />
      {c.label}
    </Badge>
  );
}

interface MeMini {
  id: string;
  name: string;
  email: string;
}

async function fetchMeMini(): Promise<MeMini> {
  const res = await fetch("/api/me", { credentials: "include" });
  if (!res.ok) throw new Error("MeFetchFailed");
  return res.json();
}

function DocViewPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data: me } = useQuery<MeMini>({
    queryKey: ["me"],
    queryFn: fetchMeMini,
    staleTime: 5 * 60_000,
  });

  const {
    data: doc,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["doc-view", id],
    queryFn: () => fetchDoc(id),
    refetchInterval: mode === "edit" ? false : 50_000,
  });

  // Todos os hooks DEVEM ser chamados antes dos early returns (regra dos hooks).
  // activeEditors filtra o próprio usuário do chip "Editando agora".
  const activeEditors = useMemo(
    () => (doc?.editors ?? []).filter((e) => !me || e.userId !== me.id),
    [doc?.editors, me],
  );

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-3 py-12 text-sm text-[var(--color-ink-muted)]">
          <Spinner size="sm" />
          Carregando documento…
        </div>
      </AppShell>
    );
  }

  if (error || !doc) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center max-w-md mx-auto">
          <div className="flex h-14 w-14 items-center justify-center rounded-md hairline bg-[var(--color-canvas)] text-[var(--color-danger)] mb-4">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h3 className="text-title text-lg text-[var(--color-ink)]">Documento não encontrado</h3>
          <p className="text-sm text-[var(--color-ink-muted)] mt-1.5">
            Pode ter sido removido, ou você não tem permissão para acessá-lo.
          </p>
          <Button asChild variant="secondary" size="sm" className="mt-5">
            <Link to="/dashboard">
              <ArrowLeft className="h-3.5 w-3.5" />
              Voltar ao painel
            </Link>
          </Button>
        </div>
      </AppShell>
    );
  }

  const isPdf = doc.mimeType === "application/pdf";
  const isImage = doc.mimeType.startsWith("image/");
  const isDocx = doc.mimeType === DOCX_MIME;
  const isExcel = doc.mimeType === XLSX_MIME || doc.mimeType === XLS_MIME;
  const canPreviewImage = isImage && doc.status === "ready" && doc.downloadUrl;
  const canRenderPdf = isPdf && doc.status === "ready";
  const canRenderSuperDoc = isDocx && doc.status === "ready";
  const canRenderExcel = isExcel && doc.status === "ready";
  const hasAnyViewer = canPreviewImage || canRenderPdf || canRenderSuperDoc || canRenderExcel;

  const canEditNow = Boolean(
    doc.permissions.canEdit && doc.status === "ready" && doc.downloadUrl && (isDocx || isExcel),
  );

  const handleEditClose = () => {
    setMode("view");
    qc.invalidateQueries({ queryKey: ["doc-view", id] });
  };

  const handleRestored = () => {
    qc.invalidateQueries({ queryKey: ["doc-view", id] });
  };

  if (mode === "edit" && (canRenderSuperDoc || canRenderExcel) && doc.downloadUrl) {
    return (
      <AppShell pageEyebrow="Editando" pageTitle={doc.name}>
        <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)] min-h-[78vh] flex flex-col">
          {canRenderSuperDoc ? (
            <SuperDocEditor
              documentId={doc.id}
              documentName={doc.name}
              downloadUrl={doc.downloadUrl}
              currentVersionNumber={doc.currentVersionNumber}
              onClose={handleEditClose}
              onOpenHistory={() => setHistoryOpen(true)}
            />
          ) : (
            <Suspense
              fallback={
                <div className="flex flex-1 items-center justify-center gap-3 py-12 text-sm text-[var(--color-ink-muted)]">
                  <Spinner size="sm" />
                  Carregando editor de planilha…
                </div>
              }
            >
              <XlsxEditor
                documentId={doc.id}
                documentName={doc.name}
                downloadUrl={doc.downloadUrl}
                currentVersionNumber={doc.currentVersionNumber}
                onClose={handleEditClose}
                onOpenHistory={() => setHistoryOpen(true)}
              />
            </Suspense>
          )}
        </div>
        <VersionHistoryPanel
          documentId={doc.id}
          documentName={doc.name}
          currentVersionNumber={doc.currentVersionNumber}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          onRestored={handleRestored}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      pageEyebrow="Documento"
      pageTitle={doc.name}
      pageActions={
        <>
          <Button asChild variant="ghost">
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Link>
          </Button>
          {doc.status === "ready" && (
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              <History className="h-4 w-4" />
              Histórico
            </Button>
          )}
          {canEditNow && (
            <Button onClick={() => setMode("edit")}>
              <Pencil className="h-4 w-4" />
              Editar
            </Button>
          )}
          {doc.status === "ready" && doc.downloadUrl && (
            <Button asChild variant={canEditNow ? "secondary" : "primary"}>
              <a href={doc.downloadUrl} target="_blank" rel="noreferrer" download={doc.name}>
                <Download className="h-4 w-4" />
                Baixar
              </a>
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4 min-w-0">
          {doc.status === "processing" && (
            <div className="rounded-md hairline bg-[var(--color-warning-wash)] border-l-2 border-l-[var(--color-warning)] px-4 py-3 flex items-center gap-3">
              <Spinner size="sm" className="text-[var(--color-warning)]" />
              <p className="text-sm text-[var(--color-ink)]">
                Verificação de segurança em andamento… A tela atualiza automaticamente quando o
                arquivo estiver liberado.
              </p>
            </div>
          )}

          {doc.status === "quarantined" && (
            <div className="rounded-md hairline bg-[var(--color-danger-wash)] border-l-2 border-l-[var(--color-danger)] px-4 py-3 flex items-start gap-3">
              <ShieldAlert
                className="h-5 w-5 mt-0.5 text-[var(--color-danger)] shrink-0"
                aria-hidden
              />
              <div>
                <p className="text-sm font-medium text-[var(--color-ink)]">
                  Arquivo isolado por segurança
                </p>
                <p className="text-sm text-[var(--color-ink-muted)] mt-1">
                  Detectamos uma possível ameaça neste arquivo. Ele foi isolado e não pode ser
                  baixado nem visualizado.
                </p>
              </div>
            </div>
          )}

          {doc.status === "rejected" && (
            <div className="rounded-md hairline bg-[var(--color-danger-wash)] border-l-2 border-l-[var(--color-danger)] px-4 py-3 flex items-start gap-3">
              <AlertCircle
                className="h-5 w-5 mt-0.5 text-[var(--color-danger)] shrink-0"
                aria-hidden
              />
              <div>
                <p className="text-sm font-medium text-[var(--color-ink)]">Arquivo recusado</p>
                <p className="text-sm text-[var(--color-ink-muted)] mt-1">
                  O envio não passou na verificação de integridade. Tente enviar novamente.
                </p>
              </div>
            </div>
          )}

          {canRenderPdf && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
              <div className="px-4 py-2.5 hairline-b">
                <p className="text-label text-[var(--color-ink-muted)]">Pré-visualização</p>
              </div>
              <PdfViewer documentId={doc.id} />
            </div>
          )}

          {canPreviewImage && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
              <div className="px-4 py-2.5 hairline-b">
                <p className="text-label text-[var(--color-ink-muted)]">Pré-visualização</p>
              </div>
              <div className="bg-[var(--color-canvas)] flex items-center justify-center p-6">
                <img
                  src={doc.downloadUrl!}
                  alt={doc.name}
                  className="max-w-full max-h-[78vh] rounded-sm shadow-[var(--shadow-soft)]"
                />
              </div>
            </div>
          )}

          {canRenderSuperDoc && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
              <div className="px-4 py-2.5 hairline-b">
                <p className="text-label text-[var(--color-ink-muted)]">Pré-visualização</p>
              </div>
              <SuperDocViewer
                documentId={doc.id}
                documentName={doc.name}
                documentMime={doc.mimeType}
              />
            </div>
          )}

          {canRenderExcel && (
            <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
              <div className="px-4 py-2.5 hairline-b">
                <p className="text-label text-[var(--color-ink-muted)]">Pré-visualização</p>
              </div>
              <ExcelViewer documentId={doc.id} />
            </div>
          )}

          {!hasAnyViewer && doc.status === "ready" && (
            <div className="rounded-md hairline bg-[var(--color-surface)] px-6 py-12 text-center shadow-[var(--shadow-soft)]">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-md hairline bg-[var(--color-canvas)] mb-4">
                {fileIconLarge(doc.mimeType)}
              </div>
              <h3 className="text-title text-lg text-[var(--color-ink)]">
                Pré-visualização indisponível
              </h3>
              <p className="text-sm text-[var(--color-ink-muted)] mt-2 max-w-sm mx-auto">
                Este formato não pode ser exibido no navegador. Baixe o arquivo para abrir no seu
                computador.
              </p>
              {doc.downloadUrl && (
                <Button asChild variant="secondary" size="sm" className="mt-5">
                  <a href={doc.downloadUrl} target="_blank" rel="noreferrer" download={doc.name}>
                    <Download className="h-4 w-4" aria-hidden />
                    Baixar arquivo
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {activeEditors.length > 0 && (
            <div className="rounded-md hairline bg-[var(--color-surface)] px-4 py-3 shadow-[var(--shadow-soft)]">
              <p className="text-label text-[var(--color-ink-muted)] mb-2">Editando agora</p>
              <ul className="flex flex-wrap items-center gap-2">
                {activeEditors.map((ed) => (
                  <li key={ed.userId} className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                      style={{ backgroundColor: ed.color }}
                      title={ed.name}
                    >
                      {initialsOf(ed.name)}
                    </span>
                    <span className="text-sm text-[var(--color-ink-2)] truncate max-w-[140px]">
                      {ed.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-md hairline bg-[var(--color-surface)] overflow-hidden shadow-[var(--shadow-soft)]">
            <div className="flex items-center gap-3 px-4 py-3.5 hairline-b">
              <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-sm bg-[var(--color-surface-2)] hairline">
                {fileIconLarge(doc.mimeType)}
              </span>
              <div className="min-w-0">
                <p className="text-label text-[var(--color-ink-muted)]">Arquivo</p>
                <p className="text-sm text-[var(--color-ink)] truncate font-medium mt-0.5">
                  {doc.name}
                </p>
              </div>
            </div>

            <dl className="divide-y divide-[var(--color-line-faint)]">
              <MetaRow label="Situação">{statusBadge(doc.status)}</MetaRow>
              <MetaRow label="Versão">
                <span className="text-sm text-[var(--color-ink-2)]">
                  v{doc.currentVersionNumber || 1}
                </span>
              </MetaRow>
              <MetaRow label="Tipo de arquivo">
                <span className="text-sm text-[var(--color-ink-2)]">
                  {friendlyMime(doc.mimeType)}
                </span>
              </MetaRow>
              <MetaRow label="Tamanho">
                <span className="text-sm text-[var(--color-ink-2)]">
                  {formatBytes(doc.sizeBytes)}
                </span>
              </MetaRow>
              <MetaRow label="Enviado em">
                <span className="text-sm text-[var(--color-ink-2)]">
                  {new Date(doc.createdAt).toLocaleString("pt-BR")}
                </span>
              </MetaRow>
            </dl>
          </div>

          <div className="rounded-md hairline bg-[var(--color-surface-2)] p-4 text-sm text-[var(--color-ink-muted)] leading-relaxed flex items-start gap-3">
            <Hash className="h-4 w-4 mt-0.5 text-[var(--color-gold)] shrink-0" aria-hidden />
            <p>
              Toda visualização e download ficam registrados no histórico do escritório. Os links de
              download expiram em 1 minuto por segurança.
            </p>
          </div>
        </aside>
      </div>

      <VersionHistoryPanel
        documentId={doc.id}
        documentName={doc.name}
        currentVersionNumber={doc.currentVersionNumber}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onRestored={handleRestored}
      />
    </AppShell>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 px-4 py-2.5 items-center">
      <dt className="text-label text-[var(--color-ink-muted)]">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
